import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, ViewContainerRef, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Overlay } from "@angular/cdk/overlay";
import {
  BaseComponent,
  UserService,
  WindowService,
  SupportService,
  ContentService,
  HelperService,
  MessageEmbed,
  WorkspaceService,
  MENTION_RESULT_LIMIT,
  MentionCandidate,
  MentionContext,
  detectMentionContext,
  isCompletedMention,
  mentionDisplayName,
  mentionSelector,
  shouldKeepMentionOpen,
} from "@mypxplat/xplat/core";
import { WebCommunityService, MentionSource, MentionSourceService } from "@mypxplat/xplat/web/core";
import type Quill from "quill";

import { MentionOverlayController, caretAnchor } from "../mention-picker/mention-overlay.controller";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-message-input",
  templateUrl: "message-input.component.html",
  standalone: false,
})
export class MessageInputComponent extends BaseComponent implements OnDestroy {
  public message: string;
  public highlightedMention: any;
  public selectedMentionsInMessage: Array<any> = [];
  public selectedArticlesInMessage: Array<any> = [];
  public selectedCategoriesSectionsInMessage: Array<any> = [];
  public selectedLearnContentInMessage: Array<any> = [];
  public publicFileLinksInMessage: Array<any> = [];
  public messageEmbeds: Array<MessageEmbed> = [];
  @Input() class: string;
  @Input() inputID: string;
  @Input() type: string;
  @Input() placeholder: string;
  @Input() ariaLabelBy: string;
  @Input() ariaDescribedBy: string;
  @Input() includeSendButton: boolean = false;
  @Input() textSendButton: boolean = false;
  @Input() includeAttachmentButton: boolean = false;
  @Input() mentionSource: any;
  @Input() mentionDataSource: Array<any>;
  @Input() shouldDetectContent: boolean;
  @Input() collaborators: Array<any>;
  @Input() useQuill: boolean;
  @Input() backgroundColor: string = "transparent";
  @Input() sendOnEnter: boolean = false;
  @Input() sendButtonLoading: boolean;
  @Output() returnPressed: EventEmitter<any> = new EventEmitter();
  @Output() valueChanged: EventEmitter<any> = new EventEmitter();
  @Output() keyUp: EventEmitter<any> = new EventEmitter();
  @Output() keyDown: EventEmitter<any> = new EventEmitter();
  @Output() blur: EventEmitter<any> = new EventEmitter();
  @Output() loaded: EventEmitter<any> = new EventEmitter();
  @Output() messageSent: EventEmitter<any> = new EventEmitter();
  @Output() focus: EventEmitter<any> = new EventEmitter();
  @Output() inputHeightChange: EventEmitter<any> = new EventEmitter();
  @Output() attachmentBtnClicked: EventEmitter<any> = new EventEmitter();
  @Output() contentDetected: EventEmitter<any> = new EventEmitter();
  @Output() mentionSelected: EventEmitter<any> = new EventEmitter();
  @Output() openUploadImage: EventEmitter<any> = new EventEmitter();
  public displayPlaceholder: boolean = true;
  public gettingFileDetails: any;
  public containerHeight: number;
  @ViewChild("container", { static: false }) container: ElementRef;
  @ViewChild("messageInput", { static: false }) messageInput: ElementRef;
  @ViewChild("messageSpan", { static: false }) messageSpan: ElementRef;

  public controlDepressed: boolean = false;
  public quill: Quill;
  private quillDestroyed = false;
  private resolveQuillReady: () => void;
  // Resolves once initQuill() has created the editor; Quill now loads asynchronously.
  private quillReady = new Promise<void>((resolve) => (this.resolveQuillReady = resolve));
  constructor(
    public userService: UserService,
    private _win: WindowService,
    public communityService: WebCommunityService,
    public supportService: SupportService,
    public contentService: ContentService,
    public helperService: HelperService,
    public workspaceService: WorkspaceService,
    private sanitizer: DomSanitizer,
    private overlayService: Overlay,
    private viewContainerRef: ViewContainerRef,
    private mentionSourceService: MentionSourceService
  ) {
    super(userService);
  }

  ngOnDestroy() {
    this.quillDestroyed = true;
    // The picker lives in a body-level overlay, so it would outlive its input.
    if (this._overlay) this._overlay.close();
    if (this.mentionSourceService) this.mentionSourceService.cancel();
    super.ngOnDestroy();
  }

  ngOnInit() {
    this.loaded.next({ id: this.inputID, cmp: this });
  }

  ngAfterViewInit() {
    this._win.setTimeout(() => {
      if (this.container && this.container.nativeElement) {
        this.containerHeight = this.container.nativeElement.clientHeight;
      }
      if (this.useQuill) this.initQuill();
    }, 300);
  }

  async initQuill() {
    // Quill is loaded on demand so it stays out of the main bundle.
    const { default: Quill } = await import("quill");
    if (this.quillDestroyed) return;
    const toolbarOptions = [["bold", "italic", "underline", "strike"], ["blockquote", "code-block"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]];

    const customBindings = {
      enter: {
        key: "Enter",
        ctrlKey: true,
        handler: (range, context) => {
          this.send();
        },
      },
    };
    this.quill = new Quill("#qe_" + this.inputID, {
      modules: {
        toolbar: toolbarOptions,
        keyboard: {
          bindings: customBindings,
        },
      },
      placeholder: "What would you like to say?",
      theme: "snow",
    });
    // Move the toolbar into the custom container
    const toolbar = (<any>this.quill.getModule("toolbar")).container;
    const customToolbarContainer = document.getElementById("qt_" + this.inputID);
    customToolbarContainer.appendChild(toolbar);

    this.quill.on("text-change", (delta, oldDelta, source) => {
      let htmlContent = this.quill.root.innerHTML;
      // Check for pasted base64 images and remove them
      if (htmlContent.includes("<img")) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlContent;

        const images = tempDiv.getElementsByTagName("img");
        let hasRemovedImages = false;

        // Remove all img tags
        while (images.length > 0) {
          images[0].remove();
          hasRemovedImages = true;
        }

        if (hasRemovedImages) {
          // Update quill content without the images
          this.quill.root.innerHTML = tempDiv.innerHTML;
          htmlContent = tempDiv.innerHTML;
          this.openUploadImage.next(
            "Sorry, but when you paste an image into this text box, it makes this post unsearchable because the textual content is too large! Please upload the image instead."
          );
        }
      }
      this.valueChanged.next(htmlContent);
    });
    this.quill.root.addEventListener("focus", () => {
      this.focus.next(true);
    });

    this.quill.root.addEventListener("blur", () => {
      this.blur.next(true);
    });
    this.resolveQuillReady();
  }

  emptyInput() {
    this.message = "";
    let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
    if (messageSpan) messageSpan.innerHTML = "";
    this.displayPlaceholder = false;
    this.closeMentions();
    this.selectedMentionsInMessage = [];
    if (this.quill) this.quill.setText("");
    this.evalContainerHeight();
  }

  keyUpEvent(args) {
    if (this.type == "span") {
      let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
      this.message = messageSpan.innerHTML;
      if (args.key == "Control") this.controlDepressed = false;
      this.displayPlaceholder = this.message ? false : true;
    }
    if (args.key == "Enter" || args.key == "Return" || args.keyCode == 13) {
      if (this.type != "span" || (this.type == "span" && (this.controlDepressed || (this.sendOnEnter && !args.shiftKey)))) {
        this.keyUp.next({ event: args, message: this.message });

        this.returnPressed.next({ value: this.message, event: args, cmp: this });
        this.messageSent.next({ value: this.message, event: args, cmp: this });
        this.controlDepressed = false;
      } else {
        this.keyUp.next({ event: args, message: this.message });
        if (this.shouldDetectContent) this.detectContent();
        if (this.elligibleMentions && this.elligibleMentions.length) args.preventDefault();
      }
    } else {
      this.keyUp.next({ event: args, message: this.message });
      if (this.shouldDetectContent) this.detectContent();
      // Navigation keys move the caret without changing the text, so the mention
      // state still has to be re-derived -- that is what closes the picker when
      // the user arrows or clicks away from what they were typing.
      if (this.type == "span" && this._isCaretMove(args)) this.detectMention();
      this.valueChanged.next(this.message);
    }

    if (!this.type) args.preventDefault();
    this.evalContainerHeight();
  }

  detectContent() {
    this._win.setTimeout(() => {
      var urlRegex = /(\bhttps?:\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
      if (this.message.indexOf("support/articles") > -1) {
        // https://sphere.presonus.com/support/articles?category=9528950167821&section=9530420849165&article=9041673905165
        let categoryId, sectionId, articleId, shareHash;
        let myUrl = this.message.match(urlRegex) && this.message.match(urlRegex)[0] ? this.message.match(urlRegex)[0] : "";
        if (myUrl.indexOf("&nbsp;")) myUrl = myUrl.replace("&nbsp;", "");
        if (myUrl) {
          let paramsStringParts = myUrl.split("&amp;");
          paramsStringParts.forEach((part) => {
            if (part.indexOf("category") > -1) {
              categoryId = part.split("=")[1];
            } else if (part.indexOf("section") > -1) {
              sectionId = part.split("=")[1];
            } else if (part.indexOf("article") > -1) {
              articleId = part.split("=")[1];
            }
          });
          if (articleId && sectionId) {
            // get the section, which will come with the article.
            this.supportService.getArticlesBySection(sectionId).subscribe((result) => {
              let article = result.filter((art) => art.id == articleId)[0];
              if (article) {
                let replaceHtml =
                  '<span contenteditable="false" class="inline-article" data-articleid="' +
                  articleId +
                  '" data-sectionid="' +
                  sectionId +
                  '" role="button" tabindex="0" aria-label="Article: ' +
                  article.name +
                  '">' +
                  article.name +
                  "</span>";
                if (!this.selectedArticlesInMessage.filter((item) => article.id == item.article.id).length) {
                  this.selectedArticlesInMessage.push({ selector: article.name, editorSelector: replaceHtml, article: article });
                }
                this.contentDetected.next({ selector: article.name, editorSelector: replaceHtml, article: article });
                this.setMessageValue(this.message.replace(myUrl, replaceHtml));
                this.valueChanged.next(this.message);
                this.cursorToEnd(50);
              }
            });
          } else if (sectionId && categoryId) {
            this.supportService.getSectionsByCategoryId(categoryId).subscribe((result) => {
              let section = result.filter((item) => item.id == sectionId)[0];
              if (section) {
                let replaceHtml =
                  '<span contenteditable="false" class="inline-article" data-categoryid="' +
                  categoryId +
                  '" data-sectionid="' +
                  sectionId +
                  '" role="button" tabindex="0" aria-label="Section: ' +
                  section.name +
                  '">' +
                  section.name +
                  "</span>";
                if (!this.selectedCategoriesSectionsInMessage.filter((item) => section.id == item.category_or_section.id).length) {
                  this.selectedCategoriesSectionsInMessage.push({ selector: section.name, editorSelector: replaceHtml, category_or_section: section });
                }
                this.contentDetected.next({ selector: section.name, editorSelector: replaceHtml, category_or_section: section });
                this.setMessageValue(this.message.replace(myUrl, replaceHtml));
                this.valueChanged.next(this.message);
                this.cursorToEnd(50);
              }
            });
          } else if (categoryId) {
            this.supportService.getCategories().subscribe((result: any) => {
              let category = result.filter((item) => item.id == categoryId)[0];
              if (category) {
                let replaceHtml =
                  '<span contenteditable="false" class="inline-article" data-categoryid="' +
                  categoryId +
                  '" role="button" tabindex="0" aria-label="Category: ' +
                  category.name +
                  '">' +
                  category.name +
                  "</span>";
                if (!this.selectedCategoriesSectionsInMessage.filter((item) => category.id == item.category_or_section.id).length) {
                  this.selectedCategoriesSectionsInMessage.push({ selector: category.name, editorSelector: replaceHtml, category_or_section: category });
                }
                this.contentDetected.next({ selector: category.name, editorSelector: replaceHtml, category_or_section: category });
                this.setMessageValue(this.message.replace(myUrl, replaceHtml));
                this.valueChanged.next(this.message);
                this.cursorToEnd(50);
              }
            });
          }
        }
      } else if (this.message.indexOf("viewingVideo=") > -1) {
        let myUrl = this.message.match(urlRegex) && this.message.match(urlRegex)[0] ? this.message.match(urlRegex)[0] : "";
        if (myUrl.indexOf("&nbsp;")) myUrl = myUrl.replace("&nbsp;", "");
        let videoId = myUrl.split("viewingVideo=")[1];
        let video = this.contentService.videosById[videoId];
        if (video) {
          let replaceHtml =
            '<span contenteditable="false" class="inline-video" data-videoid="' + video.id + '" role="button" tabindex="0" aria-label="Video: ' + video.title + '">' + video.title + "</span>";
          if (!this.selectedLearnContentInMessage.filter((item) => video.id == item.content.id).length) {
            this.selectedLearnContentInMessage.push({ selector: video.title, editorSelector: replaceHtml, content: video });
          }
          this.contentDetected.next({ selector: video.title, editorSelector: replaceHtml, content: video });
          this.setMessageValue(this.message.replace(myUrl, replaceHtml));
          this.valueChanged.next(this.message);
          this.cursorToEnd(50);
        }
      } else if (this.message.indexOf("/lesson/") > -1 || this.message.indexOf("/course/") > -1) {
        let myUrl = this.message.match(urlRegex) && this.message.match(urlRegex)[0] ? this.message.match(urlRegex)[0] : "";
        if (myUrl.indexOf("&nbsp;")) myUrl = myUrl.replace("&nbsp;", "");
        let isLesson = myUrl.split("/lesson/").length > 1;
        let itemID = isLesson ? myUrl.split("/lesson/")[1] : myUrl.split("/course/")[1];
        let item = this.contentService[isLesson ? "lessonMap" : "courseMap"][itemID];
        if (item) {
          let replaceHtml =
            '<span contenteditable="false" class="inline-lesson" data-lessonid="' +
            item.id +
            '" role="button" tabindex="0" aria-label="' +
            (isLesson ? "Lesson" : "Course") +
            ": " +
            item.title +
            '">' +
            item.title +
            "</span>";
          if (!this.selectedLearnContentInMessage.filter((item) => item.id == item.content.id).length) {
            this.selectedLearnContentInMessage.push({ selector: item.title, editorSelector: replaceHtml, content: item });
          }
          this.contentDetected.next({ selector: item.title, editorSelector: replaceHtml, content: item });
          this.setMessageValue(this.message.replace(myUrl, replaceHtml));
          this.valueChanged.next(this.message);
          this.cursorToEnd(50);
        }
      } else if (this.message.indexOf("/share/") > -1) {
        let myUrl = this.message.match(urlRegex) && this.message.match(urlRegex)[0] ? this.message.match(urlRegex)[0] : "";
        if (myUrl.indexOf("&nbsp;")) myUrl = myUrl.replace("&nbsp;", "");
        let shareHash = myUrl.split("/share/")[1];
        if (shareHash && !this.gettingFileDetails) {
          this.gettingFileDetails = shareHash;
          this.workspaceService.getFileDetails(false, shareHash).subscribe({
            next: (file: any) => {
              this.gettingFileDetails = undefined;
              file.hash = shareHash;
              let replaceHtml =
                '<span contenteditable="false" class="inline-share" data-shareid="' +
                file.hash +
                '" role="button" tabindex="0" aria-label="Shared file: ' +
                file.filename +
                '">' +
                file.filename +
                "</span>";
              if (!this.publicFileLinksInMessage.filter((item) => item.publicFile.hash == file.hash).length) {
                this.publicFileLinksInMessage.push({ selector: file.filename, editorSelector: replaceHtml, publicFile: file });
              }
              this.contentDetected.next({ selector: file.filename, editorSelector: replaceHtml, publicFile: file });
              this.setMessageValue(this.message.replace(myUrl, replaceHtml));
              this.valueChanged.next(this.message);
              this.cursorToEnd(50);
            },
            error: (err) => {
              this.gettingFileDetails = undefined;
            },
          });
        }
      }

      const newEmbeds = this.communityService.parseEmbedUrls(this.message, this.messageEmbeds);
      if (newEmbeds.length > 0) {
        this.messageEmbeds = this.messageEmbeds.concat(newEmbeds);
      }
    }, 30);
  }

  removeSelectedArticle(article) {
    this.selectedArticlesInMessage = this.selectedArticlesInMessage.filter((item) => item.article.id != article.article.id);
    if (this.message && article.selector && this.message.indexOf(article.selector) > -1) {
      this.setMessageValue(this.message.replace(article.editorSelector, ""));
      this.cursorToEnd(50);
    }
  }

  removeSelectedLearnContent(item) {
    this.selectedLearnContentInMessage = this.selectedLearnContentInMessage.filter((item) => item.content.id != item.content.id);
    if (this.message && item.selector && this.message.indexOf(item.selector) > -1) {
      this.setMessageValue(this.message.replace(item.editorSelector, ""));
      this.cursorToEnd(50);
    }
  }

  removeEmbed(embed) {
    this.messageEmbeds = this.messageEmbeds.filter(({ id }) => id != embed.id);
    if (this.message && embed.pastedUrl && this.message.includes(embed.pastedUrl)) {
      this.setMessageValue(this.message.replace(embed.pastedUrl, ""));
      this.cursorToEnd(50);
    }
  }

  removePublicFile(file) {
    this.publicFileLinksInMessage = this.publicFileLinksInMessage.filter((item) => item.publicFile.id != file.publicFile.id);
    if (this.message && file.selector && this.message.indexOf(file.selector) > -1) {
      this.setMessageValue(this.message.replace(file.editorSelector, ""));
      this.cursorToEnd(50);
    }
  }

  contentPasted(e) {
    e.preventDefault();
    let content;
    this.displayPlaceholder = false;
    if (e.clipboardData) {
      content = (e.originalEvent || e).clipboardData.getData("text/plain");
      document.execCommand("insertText", false, content);
    }
    let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
    this.message = messageSpan.innerHTML;
    this.valueChanged.next(this.message);
  }

  evalContainerHeight() {
    this._win.setTimeout(() => {
      if (this.container && this.container.nativeElement) {
        if (this.container.nativeElement.clientHeight != this.containerHeight) {
          this.containerHeight = this.container.nativeElement.clientHeight;
          this.inputHeightChange.next(this.containerHeight);
        }
      }
    }, 50);
  }

  keyDownEvent(args) {
    if (this.type == "span" && args.key == "Control") this.controlDepressed = true;

    // The picker gets first refusal on Enter/Tab/arrows/Escape while it is open,
    // so selecting a mention with Enter never also sends the message.
    if (this.type == "span" && this.mentionKeyDown(args)) return;

    if (this.type == "span" && (args.key == "Enter" || args.key == "Return" || args.keyCode == 13)) {
      if (this.controlDepressed || (this.sendOnEnter && !args.shiftKey)) {
        args.preventDefault();
      } else {
        this.keyDown.next({ event: args, message: this.message });
      }
    } else {
      this.keyDown.next({ event: args, message: this.message });
    }
  }

  /** Keys that move the caret without editing, so no `input` event fires. */
  private _isCaretMove(event: any): boolean {
    return ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"].indexOf(event.key) > -1;
  }

  /** Fired by the editor's `input` event -- every text change, however it arrived. */
  inputEvent() {
    const messageSpan = this._messageSpanEl();
    if (messageSpan) {
      this.message = messageSpan.innerHTML;
      this.displayPlaceholder = this.message ? false : true;
      this._pruneDeletedMentions(messageSpan);
    }
    this.detectMention();
    // Report every change from here, not only from keyup. keyup never reports a plain Return
    // (its Enter branch emits keyUp only), and iOS autocorrect, dictation and paste-from-menu
    // edit the text with no keyup at all — so a parent holding `valueChanged` (the post edit
    // modal saves `editedPostContent` from it) silently dropped them: added line breaks
    // "didn't save" while a deleted space in the same edit did.
    this.valueChanged.next(this.message);
  }

  /**
   * Drops records for chips the user has deleted. Keyed on the chip's element
   * rather than on a substring search, so deleting one of two mentions of the
   * same person removes exactly one record.
   */
  private _pruneDeletedMentions(editor: HTMLElement) {
    if (!this.selectedMentionsInMessage.length) return;

    const remaining: { [id: string]: number } = {};
    editor.querySelectorAll(".inline-mention[data-mentionid]").forEach((el) => {
      const id = el.getAttribute("data-mentionid");
      remaining[id] = (remaining[id] || 0) + 1;
    });

    this.selectedMentionsInMessage = this.selectedMentionsInMessage.filter((record) => {
      const id = record.user && record.user.id;
      if (!id || !remaining[id]) return false;
      remaining[id]--;
      return true;
    });
  }

  /** The caret can also move by mouse, which fires neither keyup nor input. */
  editorClicked() {
    this.detectMention();
  }

  focusIn(args) {
    this.focus.next(true);
    this.cursorToEnd();
  }

  focusOut(args) {
    if (this.message && !this.message.trim()) this.message = undefined;
    if (!this.message) this.displayPlaceholder = true;
    let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
    if (this.message != messageSpan.innerHTML) {
      this.message = messageSpan.innerHTML;
      this.valueChanged.next(this.message);
    }
    // Clicking a picker row suppresses mousedown, so focus never actually
    // leaves the editor and this cannot steal the selection out from under it.
    this.closeMentions();
    this.blur.next(true);
  }

  controlEnterEvent(event) {
    this.keyUp.next({ event: event, message: this.message });
    this.returnPressed.next({ value: this.message, event: event, cmp: this });
    event.preventDefault();
  }

  send() {
    if (!this.sendButtonLoading) {
      if (this.useQuill) {
        this.message = this.quill.root.innerHTML;
      } else {
        let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
        this.message = messageSpan.innerHTML;
      }
      this.valueChanged.next(this.message);
      this.returnPressed.next({ value: this.message, event: false, cmp: this });
      this.messageSent.next({ value: this.message, event: false, cmp: this });
    }
  }

  setMessageValue(value, focus = true) {
    this.message = value;
    this.displayPlaceholder = this.message ? false : true;
    if (this.useQuill) {
      this._win.setTimeout(async () => {
        await this.quillReady;
        if (this.quillDestroyed) return;
        const delta = this.quill.clipboard.convert({ html: value });
        this.quill.setContents(delta);
      }, 300);
    } else {
      let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
      if (messageSpan) messageSpan.innerHTML = "";
      this._win.setTimeout(() => {
        if (value && this.messageInput && this.messageInput.nativeElement) this.messageInput.nativeElement.focus();
        if (messageSpan) {
          messageSpan.innerHTML = value;
          if (value && focus) messageSpan.focus();
        }
        this.evalContainerHeight();
      }, 50);
    }
  }

  focusMessageInput() {
    if (this.useQuill) {
      // Quill is created asynchronously in ngAfterViewInit (behind a 300ms setTimeout),
      // but hosts auto-focus the input as it opens (e.g. a reply composer focuses after
      // ~200ms). That focus can win the race and run before `this.quill` exists, throwing
      // an unhandled "this.quill is undefined" TypeError. Guard it the same way emptyInput()
      // already guards this.quill. Bugsnag: 6ac15498 / 6ac02a92
      if (this.quill) this.quill.focus();
    } else {
      let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
      if (messageSpan) messageSpan.focus();
      this.cursorToEnd();
    }
  }

  cursorToEnd(timeout = 10) {
    this._win.setTimeout(() => {
      let messageSpan = document.getElementById(this.inputID ? this.inputID : "input_span");
      if (messageSpan) {
        messageSpan.focus();
        var range = document.createRange();
        range.selectNodeContents(messageSpan);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        sel.collapseToEnd();
        messageSpan.focus();
      }
      this.displayPlaceholder = false;
    }, timeout);
  }

  // ---------------------------------------------------------------------------
  // @mentions
  //
  // The detection rules live in the shared engine (`mention.utils`), the
  // candidate lookup in `MentionSourceService`, and the dropdown in a CDK
  // overlay. What is left here is only the bridge between the contenteditable
  // DOM and those three: read text + caret out, put a chip back in.
  //
  // Nothing about the query is remembered between keystrokes. It is re-derived
  // from the text every time, which is why backspacing back into a mention
  // simply re-opens it and there is no Backspace/Delete bookkeeping left.
  // ---------------------------------------------------------------------------

  public elligibleMentions: Array<MentionCandidate> = [];
  public isMentioning: boolean = false;
  public mentionQuery: string = "";
  /**
   * Offset of the "@" the user dismissed with Escape. Suppresses that one
   * mention without blocking the next.
   */
  private _dismissedTriggerIndex: number | null = null;
  private _mentionContext: MentionContext | null = null;
  private _overlay: MentionOverlayController;

  private get _mentionsAllowed(): boolean {
    return !!this.mentionSource && !this.useQuill;
  }

  private get _mentionSourceKind(): MentionSource {
    return this.mentionSource === "community_profiles" ? "community_profiles" : "collaborators";
  }

  private _ensureOverlay(): MentionOverlayController {
    if (!this._overlay) {
      this._overlay = new MentionOverlayController(this.overlayService, this.viewContainerRef, this.inputID || "mention", (candidate) => this.selectMention(candidate));
    }
    return this._overlay;
  }

  /**
   * Re-derives the mention state from the editor's text and caret.
   *
   * Driven by `input` rather than `keyup` so paste, IME composition, autocorrect
   * and undo are all handled by the same path as ordinary typing.
   */
  detectMention() {
    if (!this._mentionsAllowed) return;

    const editor = this._messageSpanEl();
    if (!editor) return;

    const caret = this._caretOffset(editor);
    const context = caret === null ? null : detectMentionContext(this._editorText(editor), caret);

    // A different "@" than the dismissed one means Escape no longer applies.
    if (this._dismissedTriggerIndex !== null && (!context || context.triggerIndex !== this._dismissedTriggerIndex)) {
      this._dismissedTriggerIndex = null;
    }

    if (!context || context.triggerIndex === this._dismissedTriggerIndex) return this.closeMentions();

    // Clicking back into the field after inserting a mention reads the inserted
    // text back as a query that still matches -- don't re-offer whoever was just
    // picked.
    if (
      isCompletedMention(
        context,
        this.selectedMentionsInMessage.map((record) => record.selector)
      )
    )
      return this.closeMentions();

    this._mentionContext = context;
    this.isMentioning = true;
    this.mentionQuery = context.query;

    // A bare "@" shouldn't dump the whole directory on screen.
    if (!context.query.trim()) return this._renderMentions([]);

    this.mentionSourceService
      .search({
        source: this._mentionSourceKind,
        query: context.query,
        dataSource: this.mentionDataSource,
        limit: MENTION_RESULT_LIMIT,
      })
      .then((results) => {
        // null means a newer keystroke already superseded this search.
        if (results === null) return;
        // The caret may have moved on while we waited.
        if (this._mentionContext !== context) return;

        if (!shouldKeepMentionOpen(context, results.length)) return this.closeMentions();
        this._renderMentions(results);
      });
  }

  private _renderMentions(results: MentionCandidate[]) {
    this.elligibleMentions = results;
    if (!results.length) {
      this._ensureOverlay().close();
      return;
    }
    this._ensureOverlay().show(caretAnchor(this._messageSpanEl()), results);
  }

  closeMentions(dismissed = false) {
    if (dismissed && this._mentionContext) {
      this._dismissedTriggerIndex = this._mentionContext.triggerIndex;
    }
    this._mentionContext = null;
    this.isMentioning = false;
    this.mentionQuery = "";
    this.elligibleMentions = [];
    this.mentionSourceService.cancel();
    if (this._overlay) this._overlay.close();
  }

  /** DOM id of the highlighted row, for aria-activedescendant on the editor. */
  get activeMentionOptionId(): string | null {
    return this._overlay ? this._overlay.activeOptionId(this.elligibleMentions) : null;
  }

  /**
   * Handles the keys the picker owns while it is open. Returns true when the key
   * was consumed, so the caller knows to stop.
   */
  mentionKeyDown(event: any): boolean {
    if (!this._mentionsAllowed) return false;

    // Escape closes even with no results showing, so a stuck picker is always
    // dismissable. This never existed before.
    if (event.key === "Escape" || event.keyCode === 27) {
      if (this.isMentioning) {
        this.closeMentions(true);
        if (event.preventDefault) event.preventDefault();
        return true;
      }
      return false;
    }

    if (!this.elligibleMentions.length) return false;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      this._moveMentionHighlight(event.key === "ArrowUp" ? -1 : 1);
      if (event.preventDefault) event.preventDefault();
      return true;
    }

    if (event.key === "Enter" || event.key === "Return" || event.keyCode === 13 || event.key === "Tab" || event.keyCode === 9) {
      const highlighted = this.elligibleMentions.filter((item) => item.selected)[0];
      if (highlighted) {
        this.selectMention(highlighted);
        if (event.preventDefault) event.preventDefault();
        return true;
      }
    }

    return false;
  }

  private _moveMentionHighlight(delta: number) {
    const items = this.elligibleMentions;
    if (!items.length) return;

    let current = items.findIndex((item) => item.selected);
    if (current === -1) current = 0;

    // Wrap, matching the previous behaviour.
    const next = (current + delta + items.length) % items.length;
    items.forEach((item, index) => (item.selected = index === next));

    this._ensureOverlay().show(caretAnchor(this._messageSpanEl()), items);
  }

  /**
   * Replaces the "@query" the user typed with an atomic mention chip.
   *
   * This is surgery on a Range rather than a rewrite of the editor's innerHTML.
   * The old version rebuilt the whole field on a timeout and then forced the
   * caret to the very end, so inserting a mention mid-sentence threw the user to
   * the bottom of their message.
   *
   * @param noQuery Insert without consuming a typed query -- used by "reply to a
   *                reply", which seeds the box with a mention.
   */
  selectMention(user: any, noQuery = false) {
    const displayName = mentionDisplayName(user);
    const selector = mentionSelector(user);
    const editor = this._messageSpanEl();

    const chip = document.createElement("span");
    chip.setAttribute("contenteditable", "false");
    chip.className = "inline-mention";
    chip.setAttribute("data-mentionid", user.id);
    chip.setAttribute("role", "button");
    chip.setAttribute("tabindex", "0");
    chip.setAttribute("aria-label", "Mention " + displayName);
    chip.textContent = selector;

    const context = this._mentionContext;
    this.closeMentions();

    if (editor) {
      if (noQuery) {
        // Seed an empty editor.
        editor.innerHTML = "";
        editor.appendChild(chip);
        editor.appendChild(document.createTextNode(" "));
        this._placeCaretAfter(chip.nextSibling);
      } else if (context) {
        this._replaceRangeWithChip(editor, context.triggerIndex, context.caretIndex, chip);
      }
      this.message = editor.innerHTML;
    }

    this.displayPlaceholder = false;
    this.valueChanged.next(this.message);
    this.evalContainerHeight();

    // `selected` is the picker's highlight state, not part of the person. It
    // would otherwise ride along into the persisted mention record.
    const { selected, ...profile } = user;

    const record = {
      user: profile,
      id: user.id,
      editorSelector: chip.outerHTML,
      selector: selector,
    };
    this.selectedMentionsInMessage.push(record);
    this.mentionSelected.next(record);
  }

  /** Swaps the [start, end) character span of the editor for `chip`. */
  private _replaceRangeWithChip(editor: HTMLElement, start: number, end: number, chip: HTMLElement) {
    const from = this._domPositionAt(editor, start);
    const to = this._domPositionAt(editor, end);
    if (!from || !to) return;

    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    range.deleteContents();
    range.insertNode(chip);

    // A trailing space keeps the caret out of the atomic chip and gives the user
    // somewhere to keep typing.
    const spacer = document.createTextNode(" ");
    chip.parentNode.insertBefore(spacer, chip.nextSibling);
    this._placeCaretAfter(spacer);
  }

  private _placeCaretAfter(node: Node | null) {
    if (!node) return;
    const range = document.createRange();
    range.setStart(node, node.nodeType === 3 ? (node as Text).length : 0);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /**
   * Turns editor HTML into the plain-text message that gets persisted, plus the
   * mention records that point into it.
   *
   * This replaces the block of `message.replace(editorSelector, selector)` that
   * was copy-pasted into all five send paths. That approach matched on a
   * hand-built HTML string and then re-found the mention by display name at
   * render time, so two people with the same name collided, a name containing
   * "$&" corrupted the output, and only the first occurrence was ever replaced.
   * Walking the DOM instead gives exact character offsets for free.
   *
   * Call this AFTER the other inline-chip replacements (articles, learn content,
   * categories), since the offsets are measured against the string it returns.
   */
  finalizeMessage(html: string, knownMentions?: Array<any>): { message: string; mentions: Array<any> } {
    // Quill is a rich-text editor and its HTML *is* the message -- lists, bold,
    // underline and the rest live in the markup. Flattening it to plain text
    // here wiped the formatting off every beta post and every beta edit
    // (PRS-2395). Mentions are disabled whenever Quill is on (`_mentionsAllowed`),
    // so there is nothing to extract from it either way.
    if (this.useQuill) return { message: html || "", mentions: [] };

    const container = document.createElement("div");
    container.innerHTML = html || "";

    // Mentions typed in this session carry their user object; when editing an
    // existing post the chips were rendered from stored records instead, so the
    // caller passes those in.
    const userById: { [id: string]: any } = {};
    (knownMentions || []).concat(this.selectedMentionsInMessage).forEach((record) => {
      if (!record) return;
      const user = record.user || record;
      if (user && user.id) userById[user.id] = user;
    });

    const mentions: Array<any> = [];
    let text = "";

    const walk = (parent: Node) => {
      Array.prototype.forEach.call(parent.childNodes, (child: Node) => {
        if (child.nodeType === 3) {
          text += child.nodeValue;
          return;
        }
        if (child.nodeType !== 1) return;

        const el = child as HTMLElement;
        const mentionId = el.getAttribute("data-mentionid");

        if (mentionId && el.classList.contains("inline-mention")) {
          const selector = el.textContent || "";
          const start = text.length;
          text += selector;
          mentions.push({
            id: mentionId,
            selector: selector,
            start: start,
            end: text.length,
            user: userById[mentionId],
          });
          return;
        }

        if (el.tagName === "BR") {
          text += "\n";
          return;
        }

        walk(el);

        // Contenteditable wraps each line in a div (or p), so those are breaks.
        if (el.tagName === "DIV" || el.tagName === "P") text += "\n";
      });
    };

    walk(container);

    // Contenteditable is liberal with &nbsp;; nobody downstream wants them.
    text = text.replace(/ /g, " ").replace(/\n+$/, "");

    return { message: text, mentions: mentions };
  }

  private _messageSpanEl(): HTMLElement | null {
    return document.getElementById(this.inputID ? this.inputID : "input_span");
  }

  /**
   * Plain text of the editor, with each atomic chip counted as its own label.
   * Matches what `_caretOffset` and `_domPositionAt` measure.
   */
  private _editorText(editor: HTMLElement): string {
    let text = "";
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let node: Node;
    while ((node = walker.nextNode())) text += node.nodeValue;
    return text;
  }

  /** Character offset of the caret within the editor, or null if not inside it. */
  private _caretOffset(editor: HTMLElement): number | null {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return null;
    // A non-collapsed selection isn't someone typing a mention.
    if (!selection.isCollapsed) return null;
    if (!editor.contains(selection.anchorNode)) return null;

    const range = selection.getRangeAt(0).cloneRange();
    const measure = document.createRange();
    measure.selectNodeContents(editor);
    measure.setEnd(range.endContainer, range.endOffset);
    return measure.toString().length;
  }

  /** Maps a character offset back onto a (text node, offset) pair. */
  private _domPositionAt(editor: HTMLElement, target: number): { node: Node; offset: number } | null {
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let consumed = 0;
    let node: Node;
    let last: Node | null = null;

    while ((node = walker.nextNode())) {
      const length = node.nodeValue.length;
      if (consumed + length >= target) return { node, offset: target - consumed };
      consumed += length;
      last = node;
    }

    if (last) return { node: last, offset: last.nodeValue.length };
    return { node: editor, offset: editor.childNodes.length };
  }
}
